import { type FC, type ReactNode } from "react";

import {
  type DocumentFragment,
  DocumentStatusEnum,
  type ProjectFragment,
} from "@app_schema";

import {
  DOCX_EXTENSION,
  DOCX_MIME_TYPE,
  PDF_EXTENSION,
  PDF_MIME_TYPE,
} from "@application/utilities/file";
import { pluralize } from "@application/utilities/pluralize";

import { FileManager } from "@styled/file_manager";
import { FilePicker } from "@styled/file_picker";
import {
  FilePickerGuide,
  type FilePickerGuideStep,
} from "@styled/file_picker_guide";
import { FilePill } from "@styled/file_pill";
import { FileQueue } from "@styled/file_queue";
import { Stack } from "@styled/stack";

import { IntakePane } from "./intake_pane";
import { filePillStatus, renderFileQueueItem } from "./shared_card_utils";

const REPORT_ACCEPT = [
  PDF_EXTENSION,
  PDF_MIME_TYPE,
  DOCX_EXTENSION,
  DOCX_MIME_TYPE,
].join(",");
const DRAFT_ACCEPT = [DOCX_EXTENSION, DOCX_MIME_TYPE].join(",");

// What a report pane says: the Final Report's, or the Drafting tab's.
export type ReportPaneCopy = {
  hero: { title: string; subtitle: string; action: string; note?: string };
  steps: [FilePickerGuideStep, FilePickerGuideStep, FilePickerGuideStep];
  title: string;
  scope: string;
  noun: [string, string];
  subtitle: ReactNode;
  // In place of `subtitle` while a file is read, when it differs.
  reading?: string;
  accept: string;
  // One file at a time, as his Word draft is.
  single?: boolean;
};

export const FINAL_REPORT_COPY: ReportPaneCopy = {
  hero: {
    title: "Upload your final report",
    subtitle:
      "Upload your completed QME report. We'll check it for issues, then you can consult the Report Assistant on your report.",
    action: "Upload report",
  },
  steps: [
    { label: "Upload your report", description: "PDF or Word" },
    { label: "We analyze it", description: "Check for inconsistencies" },
    { label: "Examine with the Assistant", description: "Quality check" },
  ],
  title: "Report",
  scope: "Report",
  noun: ["report", "reports"],
  subtitle: (
    <>
      Upload your completed QME report — we'll check it for issues, then you can{" "}
      <strong>consult the Report Assistant on your report</strong>.
    </>
  ),
  accept: REPORT_ACCEPT,
};

// While his Word file, uploaded or the default, is read.
export const READING_DRAFT =
  "Reading your Word file. You can add sections once it's ready.";

const DRAFT_SUBTITLE =
  "Upload your Word file. Add sections where you want text, and the Drafting Assistant writes them in, ready to download.";

export const DRAFT_COPY: ReportPaneCopy = {
  hero: {
    title: "Set up your template once",
    subtitle:
      "Every new case starts from it. You add the sections here and pick what each one reads, so you don't type markers in Word.",
    action: "Upload your template",
    note: "Word (.docx)",
  },
  steps: [
    { label: "Upload your template", description: "Word, once" },
    { label: "Add your sections", description: "Click where each one goes" },
    {
      label: "Draft on every case",
      description: "From the sources you picked",
    },
  ],
  title: "Draft",
  scope: "Drafting",
  noun: ["draft", "drafts"],
  subtitle: DRAFT_SUBTITLE,
  reading: READING_DRAFT,
  accept: DRAFT_ACCEPT,
  single: true,
};

// The Drafting tab's own parts of its upload pane: in place of the upload card
// while there are no files (`empty`), another way to start under its action
// (`secondary`), the card's last line, and whether his draft is still read.
export type DraftingPaneParts = {
  empty?: ReactNode;
  secondary?: ReactNode;
  footnote?: ReactNode;
  reading: boolean;
};

// A report bucket — the Final Report, or the Drafting tab's draft — hosted in
// the guided-intake pane. Upload/processing internals (FilePicker +
// FilePickerGuide, FilePill rows, FileQueue) are reused verbatim from the prior
// FinalReportCard — only the StageCard chrome is swapped for the flat
// IntakePane header + approved copy.
export const FinalReportPane: FC<{
  project: ProjectFragment;
  documents: DocumentFragment[];
  save: (blob: { signed_id: string }) => Promise<void>;
  // Mirrors the composer's scope gate (e.g. project.hasProcessedFinalReport).
  available: boolean;
  copy: ReportPaneCopy;
  drafting?: DraftingPaneParts;
}> = ({ project, documents, save, available, copy, drafting }) => {
  const { empty, secondary, footnote } = drafting ?? {};
  const failedCount = documents.filter(
    (doc) => doc.status === DocumentStatusEnum.Failed,
  ).length;
  const hasFailures = failedCount > 0;

  const reading = drafting?.reading && copy.reading;
  const subtitle = hasFailures
    ? `${failedCount} ${pluralize(failedCount, ...copy.noun)} failed — re-upload below`
    : reading || copy.subtitle;

  // Empty, the tab is one thing to do: the upload card, then what happens.
  if (documents.length === 0 && empty) return empty;
  if (documents.length === 0) {
    return (
      <FileManager save={save}>
        <FilePicker
          hero
          title={copy.hero.title}
          subtitle={copy.hero.subtitle}
          action={copy.hero.action}
          note={copy.hero.note}
          accept={copy.accept}
          single={copy.single}
          secondary={secondary}
          footnote={footnote}
        >
          <FilePickerGuide steps={copy.steps} />
        </FilePicker>
        <FileQueue render={renderFileQueueItem} />
      </FileManager>
    );
  }

  return (
    <FileManager save={save}>
      <IntakePane
        title={copy.title}
        subtitle={subtitle}
        tone={hasFailures ? "error" : "default"}
        scope={{ label: copy.scope, locked: !available }}
      >
        <Stack>
          <FileQueue render={renderFileQueueItem} />

          {documents.map((document) => (
            <FilePill
              key={document.id}
              name={document.name}
              type={document.file.type}
              size={document.file.bytesize}
              status={filePillStatus(document)}
              to={`/dashboard/projects/${project.id}/documents/${document.id}`}
            />
          ))}
        </Stack>
      </IntakePane>
    </FileManager>
  );
};
